import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { ActivatedRoute, Router } from '@angular/router';
import { IRendererDetails, ListRenderersRequest, SituAnalyticsWebApiClient, RendererStatus, ListRenderersResponse } from 'situanalytics-webapi-client';
import { MatDialog } from '@angular/material/dialog';
import { RendererEditDialogComponent } from '../renderer-edit-dialog/renderer-edit-dialog.component';
import { isVirtualRenderer } from 'src/app/shared/models/renderer-type';

@Component({
  selector: 'app-renderer-list',
  templateUrl: './renderer-list.component.html',
  styleUrls: ['./renderer-list.component.scss']
})
export class RendererListComponent {
  readonly isVirtual = isVirtualRenderer;

  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100],
    displayedColumns: ['name']
  } as ListViewControls;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  isBusyOnly: boolean = false;
  poolId: string | null = null;
  poolName: string | null = null;
  previousSearchTerm = "";
  searchTerm = "";
  admin = false;
  renderersDataSource = new MatTableDataSource<IRendererDetails>();
  rendererStatus = RendererStatus;

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private analyticsApi: SituAnalyticsWebApiClient,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private dialog: MatDialog) {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
    if (this.route.snapshot.queryParams['isBusyOnly']) {
      this.isBusyOnly = this.route.snapshot.queryParams['isBusyOnly'] === 'true';
    }
    if (this.route.snapshot.queryParams['poolId']) {
      this.poolId = this.route.snapshot.queryParams['poolId'];
    }

    this.loadRenderers();
  }

  loadRenderers() {
    this.loading = true;

    let request = new ListRenderersRequest();

    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    // Use a larger page size when filtering by pool to ensure we get all renderers
    request.pageSize = this.poolId ? 100 : this.viewControls.pageSize;
    request.searchTerm = this.searchTerm;
    request.isBusyOnly = this.isBusyOnly;
    request.includeLatestJob = true;
    this.previousSearchTerm = this.searchTerm;

    return this.analyticsApi.listRenderers(request)
      .then((response: ListRenderersResponse) => {
        let items = response.items ?? [];
        
        // Filter by pool ID if specified
        if (this.poolId) {
          items = items.filter(renderer => renderer.rendererPool?.id === this.poolId);
          // Extract pool name from the first renderer with this pool
          const rendererWithPool = items.find(r => r.rendererPool?.id === this.poolId);
          this.poolName = rendererWithPool?.rendererPool?.name || null;
        }
        
        this.renderersDataSource.data = items.sort((a, b) => { return this.sortRenderers(a, b); });
        this.viewControls.totalItems = this.poolId ? items.length : (response.totalItems ?? 0);
        this.loading = false;
      }, error => {
        this.loading = false;
      });
  }

  updateParams() {
    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true
    });

    const queryParams: any = {};
    if (this.isBusyOnly) {
      queryParams.isBusyOnly = 'true';
    }
    if (this.poolId) {
      queryParams.poolId = this.poolId;
    }
    urlTree.queryParams = queryParams;

    this.router.navigateByUrl(urlTree);
  }

  private sortRenderers(a: IRendererDetails, b: IRendererDetails) {
    // If both items have the same isBusy value, compare based on name
    return (a.name ?? '').localeCompare(b.name ?? '');
  }

  onViewRenderer(rendererId: string) {
    this.router.navigate([`/admin/renderers/${rendererId}/dashboard`]);
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadRenderers();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  shortenGpu(gpu: string) {
    return gpu?.replace('NVIDIA GeForce', '');
  }

  getEnabledQueueCount(renderer: IRendererDetails) {
    return renderer?.rendererQueues?.filter(x => x.enabled).length;
  }

  storageRemaining(item: IRendererDetails) {
    if (item?.hardDriveCapacity && item?.hardDriveUsed) {
      const capacity = Number(item.hardDriveCapacity);
      const used = Number(item.hardDriveUsed);
      
      const remaining = capacity - used;
      return (remaining / 1e9).toFixed(2) + ' GB';
    }
    
    return "N/A";
  }

  getStorageClass(item: IRendererDetails): string {
    if (item?.hardDriveCapacity && item?.hardDriveUsed) {
      const capacity = Number(item.hardDriveCapacity);
      const used = Number(item.hardDriveUsed);
      const remaining = capacity - used;
      const remainingGB = remaining / 1e9;
      return remainingGB < 200 ? 'text-danger' : 'text-success';
    }
    return 'text-info';
  }

  getOSIcon(item: IRendererDetails): { iconPath: string, tooltip: string } | null {
    if (!item?.operatingSystem) {
      return null; // Don't show anything for unknown OS
    }
    
    const os = item.operatingSystem;
    
    // Check if it's a Windows OS
    if (os.includes('Win32NT')) {
      // Extract the build number (e.g. from "Win32NT_10.0.22631.0" get "22631")
      const parts = os.split('.');
      if (parts.length >= 3) {
        const buildNumber = parseInt(parts[2], 10);
        
        // Windows 11 starts from build 22000
        if (buildNumber >= 22000) {
          return { iconPath: '/assets/images/windows-11.svg', tooltip: 'Windows 11' };
        } else if (buildNumber >= 10000) { // Windows 10 builds
          return { iconPath: '/assets/images/windows-10.svg', tooltip: 'Windows 10' };
        }
      }
    }
    
    // Return null for any other case (not Win10 or Win11)
    return null;
  }

  getRendererStatusColour(rendererStatus: RendererStatus) {
    switch (rendererStatus) {
      case RendererStatus.Idle:
        return 'success';
      case RendererStatus.Busy:
        return 'danger';
      case RendererStatus.Down:
        return 'danger';
      case RendererStatus.Updating:
        return 'info';
      default:
        return 'muted.';
    }
  }

  getRendererStatus(rendererStatus: RendererStatus): string {
    switch (rendererStatus) {
      case RendererStatus.Idle:
        return 'Idle';
      case RendererStatus.Busy:
        return 'Busy';
      case RendererStatus.Down:
        return 'Down';
      case RendererStatus.Updating:
        return 'Updating';
      default:
        return 'N/A';
    }
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  getHealthCheckDisplay(healthCheckDate: Date) {
    if (this.isFromYesterday(healthCheckDate)) {
      return formatDate(healthCheckDate, 'shortDate', this.locale);
    }

    return formatDate(healthCheckDate, 'shortTime', this.locale);
  }

  private isFromYesterday(date: Date) {
    const now = new Date();
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return date < yesterday;
  }


  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }

  createRenderer(): void {
    const dialogRef = this.dialog.open(RendererEditDialogComponent, {
      width: '600px',
      data: { isNew: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadRenderers();
      }
    });
  }

  clearPoolFilter(): void {
    this.poolId = null;
    this.poolName = null;
    this.updateParams();
    this.loadRenderers();
  }
}

interface ListViewControls {
  pageSize: number;
  pageIndex: number;
  pageNumber: number;
  totalItems: number;
  pageSizeOptions: number[];
  displayedColumns: string[];
}
